"use client";

import Image from "next/image";
import { isDrawnAvatar as isAvatar } from "@/lib/avatar/catalog";
import AvatarCharacter from "./AvatarCharacter";

// Une vraie photo, pas l'image par défaut posée sur les comptes sans photo.
const isRealPhoto = (value) => typeof value === "string" && value !== "" && !value.endsWith("default.png");

// L'image d'un membre, partout sur le site. Le composant prend la place d'un
// <Image> ou d'un <img> sans rien changer autour : mêmes props (alt, width, height,
// className, onClick...), seul `src={membre.photo}` devient `user={membre}`.
//
// Deux contextes :
// - communauté et jeu (par défaut) : l'avatar composé l'emporte sur la photo ;
// - contexte professionnel (`preferPhoto` : mentors, appels, auteurs, témoignages,
//   correcteurs) : la vraie photo l'emporte, l'avatar ne sert que s'il n'y en a pas.
//
// L'avatar reste fixe, sauf là où `animated` le demande (profil, classement).
// `plain` garde un <img> là où il y en avait un, au lieu de next/image.
export default function UserAvatar({ user, avatar, photo, plain = false, preferPhoto = false, animated = false, interactive, reactionLabel, mood, ...props }) {
    const src = photo ?? user?.photo;
    const drawn = isAvatar(avatar) ? avatar : isAvatar(user?.avatar) ? user.avatar : null;
    const composed = preferPhoto && isRealPhoto(src) ? null : drawn;

    if (composed) {
        // Ce qui ne concerne que le chargement d'une image n'a pas de sens pour un dessin.
        const { alt, width, height, fill, sizes, priority, unoptimized, loading, quality, placeholder, blurDataURL, onError, onLoad, className = "", ...rest } = props;
        return (
            <AvatarCharacter
                avatar={composed}
                seed={user?.userId ?? user?.id ?? user?.username}
                size={typeof width === "number" ? width : undefined}
                animated={animated}
                interactive={interactive}
                reactionLabel={reactionLabel}
                mood={mood}
                title={alt}
                className={fill ? `absolute inset-0 h-full w-full ${className}` : className}
                {...rest}
            />
        );
    }

    if (plain) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img src={src} alt={props.alt ?? ""} {...props} />;
    }
    return <Image src={src} alt={props.alt ?? ""} {...props} />;
}
